/**
 * Tuile d'indicateur. Libellé et infobulle viennent du catalogue des
 * métriques, en i18n (`metric.<ID>.label` / `.help`) : un même indicateur a le
 * même nom et la même définition partout.
 */
import React from 'react';
import { Card, Skeleton, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { INK, STATE_COLORS, cardStyle } from '../monitoringTheme';
import HelpIcon from './HelpIcon';
import type { MetricId } from './i18nCatalog';
import { useFormat } from './format';

const { Text } = Typography;

export type StatTileProps = {
    metricId: MetricId;
    value: React.ReactNode;
    /** Valeurs d'interpolation du libellé (ex. `{ days: 7 }`). */
    labelValues?: Record<string, unknown>;
    /** Métrique de stock : libellée « au JJ/MM/AAAA » (D-01). */
    asOf?: string;
    /** Précision sous la valeur. */
    extra?: React.ReactNode;
    /** Appels non valorisables (IA-03), affichés quand le serveur les fournit. */
    unpriced?: { no_price: number; no_usage: number };
    loading?: boolean;
    icon?: React.ReactNode;
};

const StatTile: React.FC<StatTileProps> = ({ metricId, value, labelValues, asOf, extra, unpriced, loading, icon }) => {
    const { t } = useTranslation();
    const f = useFormat();
    const label = t(`metric.${metricId}.label`, labelValues);
    const help = t(`metric.${metricId}.help`, labelValues);

    return (
        <Card
            size="small"
            style={{ ...cardStyle, height: '100%' }}
            styles={{ body: { padding: '14px 16px' } }}
            data-metric={metricId}
        >
            <Space direction="vertical" size={2} style={{ width: '100%' }}>
                <Space size={6} align="center" wrap>
                    {icon && <span style={{ color: INK.muted, fontSize: 13, display: 'inline-flex' }}>{icon}</span>}
                    <Text style={{ fontSize: 12, color: INK.secondary, fontWeight: 500 }}>{label}</Text>
                    <HelpIcon text={help} />
                </Space>
                {asOf && (
                    <Text style={{ fontSize: 11, color: INK.muted }}>{t('monitoring.asOf', { date: f.date(asOf) })}</Text>
                )}

                {loading ? (
                    <Skeleton.Input active size="small" style={{ width: 90 }} />
                ) : (
                    <div style={{ fontSize: 22, lineHeight: 1.25, fontWeight: 700, color: INK.primary }}>{value}</div>
                )}

                {!loading && extra && <Text style={{ fontSize: 11, color: INK.muted }}>{extra}</Text>}
                {!loading && unpriced && (
                    <Text style={{ fontSize: 11, color: STATE_COLORS.warning }}>
                        {t('monitoring.unpriced.summary', {
                            noPrice: f.compact(unpriced.no_price).text,
                            noUsage: f.compact(unpriced.no_usage).text,
                        })}
                    </Text>
                )}
            </Space>
        </Card>
    );
};

export default StatTile;
